import {
  ClientFormData,
  getClientSchema,
} from '@/types/clients/ClientFormData';
import { useDistributorLabels } from '@/hooks/useDistributorLabels';
import { ClientResponse } from '@/types/clients/ClientResponse';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import StyledContainer from '../styled/StyledContainer';
import StyledFormHeaderWithBreadcrumb from '../styled/StyledFormHeaderWithBreadcrumb';
import StyledButton from '../styled/StyledButton';
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg?react';
import PlusIcon from '@/assets/icons/plus.svg?react';
import SaveIcon from '@/assets/icons/save-02.svg?react';
import ClientForm from './ClientForm';
import { useMemo, useState } from 'react';
import StyledButtonLoader from '../styled/StyledButtonLoader';
import {
  useDeleteClient,
  useFindClients,
  useUpdateClient,
} from '@/hooks/useAgencyClient';
import { useGetMe } from '@/api/user.api';
import { useToast } from '@/hooks/useToast';
import ModalDeletion from '../ui/ModalDeletion';
import { PATHS } from '@/constants/Paths';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { loadClientData } from '@/utils/clients/loadClientData';
import { formatClientRef } from '@/utils/displayCodes';
import ListProjectsPage from '../projects/ListProjectsPage';
import CreateProjectModal from '../projects/CreateProjectModal';
import { useTranslation } from '@/i18n';
import { MarginRateSchema } from '@/types/common/margin-rate.schema';

interface ViewClientFormProps {
  clientResponse: ClientResponse;
  countProjects: number;
}

type tabOptions = 'generalInfo' | 'projects' | 'reservations';

const ViewClientForm = ({
  clientResponse,
  countProjects,
}: ViewClientFormProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isTourOperator, tLabel } = useDistributorLabels();

  const methods = useForm<ClientFormData>({
    mode: 'onChange',
    resolver: zodResolver(getClientSchema(isTourOperator)),
    defaultValues: loadClientData(clientResponse),
  });

  const {
    control,
    reset,
    formState: { dirtyFields },
    handleSubmit,
  } = methods;

  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [isDeletionModalOpen, setIsDeletionModalOpen] = useState(false);
  const [isProjectCreationModalOpen, setIsProjectCreationModalOpen] =
    useState(false);
  const [currTab, setCurrTab] = useState<tabOptions>('projects');

  const { data: userData, isSuccess } = useGetMe();
  const isAdmin = useMemo(
    () => isSuccess && userData.isAdmin,
    [isSuccess, userData?.isAdmin],
  );

  const { data: agents } = useFindClients({}, (data) =>
    data.agents.map((agent) => ({
      value: agent.id,
      label: `${agent.firstname} ${agent.lastname}`,
    })),
  );

  const { mutateAsync: updateClientData } = useUpdateClient();
  const { mutateAsync: deleteClientData } = useDeleteClient();
  const { toastAsync } = useToast();

  const submitForm: SubmitHandler<ClientFormData> = async (data) => {
    const addIfDirty = <T,>(field: keyof ClientFormData): T | undefined =>
      dirtyFields[field] ? (data[field] as T) : undefined;

    const parsedMarginRate = MarginRateSchema.safeParse(data.marginRate);
    const { status } = await toastAsync(
      updateClientData({
        clientId: clientResponse.id,
        clientData: {
          secondaryContacts: data.secondaryContacts.map(
            (contact, contactIndex) => ({
              ...contact,
              id:
                clientResponse.contacts.length > contactIndex
                  ? clientResponse.contacts[contactIndex].id
                  : undefined,
            }),
          ),
          mainContact: dirtyFields.mainContact
            ? {
                firstName: dirtyFields.mainContact.firstName
                  ? data.mainContact.firstName
                  : undefined,
                lastName: dirtyFields.mainContact.lastName
                  ? data.mainContact.lastName
                  : undefined,
                email: dirtyFields.mainContact.email
                  ? data.mainContact.email
                  : undefined,
                phoneNumber: dirtyFields.mainContact.phoneNumber
                  ? data.mainContact.phoneNumber
                  : undefined,
              }
            : undefined,
          address: addIfDirty('address'),
          city: addIfDirty('city'),
          postalCode: addIfDirty('postalCode'),
          country: dirtyFields.country?.value ? data.country?.value : undefined,
          marginRate: parsedMarginRate.success
            ? parsedMarginRate.data
            : undefined,
          agencyName: addIfDirty('agencyName'),
          reservationAgentId: isAdmin
            ? dirtyFields.reservationAgent?.value
              ? data.reservationAgent.value
              : undefined
            : undefined,
        },
      }),
      {
        success: tLabel('clientUpdatedSuccess'),
      },
    );

    if (status < 300) setIsEditEnabled(false);
  };

  return (
    <FormProvider {...methods}>
      <StyledContainer>
        <StyledFormHeaderWithBreadcrumb
          title={
            isTourOperator && clientResponse.agencyName
              ? clientResponse.agencyName
              : `${clientResponse.mainContact.firstName} ${clientResponse.mainContact.lastName}`
          }
          code={formatClientRef(clientResponse.ref)}
          breadcrumb={
            <StyledButton
              variant="link"
              title={tLabel('backToList')}
              className="text-sm text-[#3D3D3D] pl-0"
              onClick={() => navigate(PATHS.distributors.clients.list)}
              icon={<ArrowLeftIcon />}
            />
          }
        >
          {currTab === 'generalInfo' && (
            <>
              <StyledButton
                title={tLabel('delete')}
                onClick={() => {
                  setIsDeletionModalOpen(true);
                }}
                variant="outlined"
                className="font-semibold gap-1 px-3 py-2 text-black border-gray-300"
              />
              {isEditEnabled ? (
                <>
                  <StyledButton
                    title={t('common.cancel')}
                    onClick={() => {
                      reset();
                      setIsEditEnabled(false);
                    }}
                    className="font-semibold gap-1 px-3 py-2"
                    variant="outlined"
                  />
                  <StyledButtonLoader
                    title={t('common.save')}
                    successTitle={tLabel('informationSaved')}
                    onClick={handleSubmit(submitForm)}
                    className="font-semibold gap-1 px-3 py-2"
                    icon={<SaveIcon className="size-5" />}
                  />
                </>
              ) : (
                <StyledButton
                  title={tLabel('editInformation')}
                  onClick={() => {
                    setIsEditEnabled(true);
                  }}
                  className="font-semibold gap-1 px-3 py-2"
                  variant="outlined"
                />
              )}
            </>
          )}
          {!isEditEnabled && (
            <StyledButton
              title={tLabel('newProject')}
              icon={<PlusIcon className="w-5 h-5 fill-white" />}
              className="font-semibold gap-1 px-3 py-2"
              onClick={() => {
                setIsProjectCreationModalOpen(true);
              }}
            />
          )}
        </StyledFormHeaderWithBreadcrumb>
        <Tabs
          defaultValue="generalInfo"
          value={currTab}
          className="w-full"
          onValueChange={(value) => {
            setCurrTab(value as tabOptions);
          }}
        >
          <div className="flex flex-col w-full h-auto px-6 gap-6">
            <div className="flex w-full justify-between">
              <TabsList>
                <TabsTrigger
                  value="generalInfo"
                  className="hover:cursor-pointer"
                >
                  {tLabel('generalInfo')}
                </TabsTrigger>
                <TabsTrigger
                  value="projects"
                  className="hover:cursor-pointer flex items-center justify-center gap-2"
                >
                  <span>{tLabel('projects')}</span>
                  <div className="size-5 flex justify-center items-center text-brand-100 bg-brand-500 border border-brand-600 rounded-2xl px-1.5 py-0.5">
                    {countProjects}
                  </div>
                </TabsTrigger>
              </TabsList>
            </div>
          </div>
          <TabsContent value="generalInfo">
            <ClientForm
              control={control}
              isViewMode={!isEditEnabled}
              agents={agents}
              isAdmin={isAdmin}
              isTourOperator={isTourOperator}
            />
          </TabsContent>
          <TabsContent value="projects">
            <ListProjectsPage
              clientId={clientResponse.id}
              isTourOperator={isTourOperator}
            />
          </TabsContent>
        </Tabs>
      </StyledContainer>
      {isDeletionModalOpen && (
        <ModalDeletion
          actionOnClick={async () => {
            const { status } = await toastAsync(
              deleteClientData(clientResponse.id),
              {
                success: tLabel('clientDeletedSuccess'),
              },
            );
            if (status < 300) navigate(PATHS.distributors.clients.list);
          }}
          closeModal={() => {
            setIsDeletionModalOpen(false);
          }}
          type="client"
        />
      )}
      {isProjectCreationModalOpen && (
        <CreateProjectModal
          clientId={clientResponse.id}
          isTourOperator={isTourOperator}
          closeModal={() => {
            setIsProjectCreationModalOpen(false);
          }}
          onSuccess={() => {
            setCurrTab('projects');
          }}
        />
      )}
    </FormProvider>
  );
};

export default ViewClientForm;
